import { driveKindFromMime, MAX_HIVE_UPLOAD_BYTES, type ItemType } from '@hive/shared';
import { ensureFolderPath, uploadFile, type DriveClient } from '@/lib/drive';
import type { compressImage } from '@/lib/images';
import type { ItemRef, saveVaultLink, uploadToHive } from '@/data/vault';

/** Where an upload is filed: its game and the module it came from (Drive: Hive Uploads / game / module). */
export interface UploadContext {
  gameId: string | null;
  gameSlug: string | null;
  gameName: string;
  module: 'Grid' | 'Forge' | 'Vault' | 'Comms';
  /** The item the upload attaches to, if any. */
  attachTo?: ItemRef;
  /** Hidden properties for a Drive upload (so Hive can find the copy again, e.g. a Slack file's id). */
  driveProperties?: Record<string, string>;
}

export type Destination = 'hive' | 'drive';

/** Why an upload can't go where it's set to, or null if it can. */
export function destinationProblem(
  file: { type: string; size: number },
  destination: Destination,
  drive: { connected: boolean; rootFolderId: string | null },
): string | null {
  if (destination === 'hive' && file.size > MAX_HIVE_UPLOAD_BYTES) {
    return 'Hive storage takes files up to 25 MB. Send this one to Drive.';
  }
  if (destination === 'drive' && !drive.connected) return 'Connect Google Drive first (account menu).';
  if (destination === 'drive' && !drive.rootFolderId) return 'Pick the Hive Uploads folder in Vault first.';
  return null;
}

export interface UploadDeps {
  drive: DriveClient;
  saveLink: typeof saveVaultLink;
  toHive: typeof uploadToHive;
  compress: typeof compressImage;
  link: (from: ItemRef, to: ItemRef) => Promise<void>;
}

/**
 * Sends one file where it's going and attaches the result to `ctx.attachTo`:
 * Hive storage (images shrunk to WebP first) as a `files` row, or straight to
 * Drive with a resumable upload as a `vault_links` row.
 */
export async function uploadAttachment(
  file: File,
  destination: Destination,
  ctx: UploadContext,
  driveRootFolderId: string | null,
  onProgress: (fraction: number) => void,
  deps: UploadDeps,
): Promise<ItemRef & { type: Extract<ItemType, 'file' | 'vault_link'> }> {
  let result: ItemRef & { type: 'file' | 'vault_link' };
  if (destination === 'hive') {
    const image = file.type.startsWith('image/') ? await deps.compress(file).catch(() => null) : null;
    onProgress(0.3);
    const row = await deps.toHive(image?.file ?? file, {
      gameId: ctx.gameId,
      gameSlug: ctx.gameSlug,
      ...(image ? { width: image.width, height: image.height } : {}),
    });
    result = { type: 'file', id: row.id };
  } else {
    if (!driveRootFolderId) throw new Error('Pick the Hive Uploads folder in Vault first.');
    const folder = await ensureFolderPath(deps.drive, driveRootFolderId, [ctx.gameName, ctx.module]);
    const uploaded = await uploadFile(
      deps.drive,
      file,
      folder,
      (f) => onProgress(f * 0.95),
      undefined,
      ctx.driveProperties,
    );
    const row = await deps.saveLink({
      url: uploaded.webViewLink ?? `https://drive.google.com/file/d/${uploaded.id}/view`,
      title: uploaded.name,
      kind: driveKindFromMime(uploaded.mimeType || file.type),
      provider_id: uploaded.id,
      preview: { mime_type: uploaded.mimeType || file.type, size_bytes: file.size },
      game_id: ctx.gameId,
    });
    result = { type: 'vault_link', id: row.id };
  }
  if (ctx.attachTo) await deps.link(ctx.attachTo, result);
  onProgress(1);
  return result;
}
